import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLine } from "./Reveal";
import { Phone, ArrowRight, MapPin } from "lucide-react";
import logo from "../../assets/logo.png";

const HERO_IMG =
  "https://images.pexels.com/photos/9667550/pexels-photo-9667550.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.62, 0.9]);
  const logoY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen overflow-hidden grain" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Pressure wash water spray" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-fr-black" />
      <div className="absolute inset-0 bg-gradient-to-t from-fr-black via-fr-black/30 to-fr-black/70" />

      {/* Floating brand badge */}
      <motion.div
        style={{ y: logoY }}
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.6, duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="hidden lg:block absolute right-4 xl:right-20 top-1/2 -translate-y-1/2 z-[5] pointer-events-none"
      >
        <div className="absolute inset-0 bg-fr-blue/25 blur-[90px] rounded-full" />
        <motion.img
          src={logo}
          alt="First Response Exterior Cleaning LLC"
          animate={{ y: [0, -14, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="relative w-[34vw] max-w-[500px] drop-shadow-[0_20px_40px_rgba(0,0,0,0.5)]"
        />
      </motion.div>


      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 min-h-screen flex flex-col justify-center pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-fr-blue" />
          <span className="fr-mono text-xs tracking-[0.3em] uppercase text-fr-blue-light flex items-center gap-2">
            <MapPin size={13} /> Serving all of Charleston, SC
          </span>
        </motion.div>

        <h1 className="font-display font-bold text-white text-[3rem] leading-[0.95] sm:text-7xl lg:text-8xl tracking-tight max-w-5xl [word-spacing:0.12em]">
          <MaskLine delay={0.25}>WE CLEAN IT LIKE</MaskLine>
          <MaskLine delay={0.38} className="text-fr-blue-light">{"WE'RE RESPONDING"}</MaskLine>
          <MaskLine delay={0.51}>TO OUR OWN HOME.</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.7 }}
          className="mt-8 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed"
        >
          Professional soft washing, driveway & walkway cleaning, porch painting and deck
          staining. Fast, reliable, and satisfaction guaranteed — every single job.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.7 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a href="#estimate" data-testid="hero-estimate-btn" className="btn-primary inline-flex items-center justify-center gap-2 group">
            Get a Free Estimate
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
          <a href="tel:8432975559" data-testid="hero-call-btn" className="btn-outline inline-flex items-center justify-center gap-2">
            <Phone size={17} /> 843-297-5559
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 fr-mono text-[10px] tracking-[0.3em] text-white/40 uppercase"
      >
        Scroll to explore
      </motion.div>
    </section>
  );
};
